import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Figure from '../Figure';
import MediaPlayer from '../MediaPlayer';
import Typo from '../Typo';
import Carousel from '../Carousel';
import AudioPlayer from '../MediaPlayer/AudioPlayer';
import EmbedModule from '../../features/Module/components/EmbedModule';
import theme from '../theme';
import { getLighterThemeColor } from '../theme/colors';

// Component that hides the entire figure container if image fails to load
const SingleImage = ({ file }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) return null;

  return (
    <Figure.Rectangle $objectfit='contain'>
      <img
        src={file.url}
        alt={file.title || file.name || 'image'}
        onError={() => setHasError(true)}
      />
    </Figure.Rectangle>
  );
};

// Carousel item that hides itself if image fails to load
const CarouselImageItem = ({ file }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) return null;

  return (
    <div>
      <Figure.Rectangle $objectfit='contain'>
        <img
          src={file.url}
          alt={file.title || file.name || 'image'}
          onError={() => setHasError(true)}
        />
      </Figure.Rectangle>
      {(file.title || file.name) && <Typo.FigCaption>{file.title || file.name}</Typo.FigCaption>}
    </div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CarouselWrapper = styled.div`
  background-color: ${({ $bgColor }) => $bgColor || theme.colors.NEU_50};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`;

const VideoContainer = styled.div`
  width: 100%;
`;

// File type detection utilities
const getFileType = (file) => {
  // Check mime type first (most reliable)
  const mime = file.mime || file.mimeType || '';
  if (mime) {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
  }

  // Check type property from API
  if (file.type) {
    if (file.type.includes('video')) return 'video';
    if (file.type.includes('image')) return 'image';
    if (file.type.includes('audio')) return 'audio';
    if (file.type === 'external' || file.provider) return 'embed';
    if (file.type === 'other') return 'other';
  }

  // Check for embed/external
  if (file.provider) return 'embed';

  // Fallback to URL/name/title extension detection (handle query params)
  const url = file.url || file.name || file.title || '';
  const urlWithoutParams = url.split('?')[0]; // Remove query parameters
  if (/\.(jpeg|jpg|gif|png|webp|svg|avif)$/i.test(urlWithoutParams)) return 'image';
  if (/\.(mp4|ogv|webm|mov|m4v)$/i.test(urlWithoutParams)) return 'video';
  if (/\.(mp3|wav|aac|flac|ogg)$/i.test(urlWithoutParams)) return 'audio';

  return 'other';
};

/**
 * FileMedia - Component for displaying layout media (images, videos, audio)
 * For attachments that need download links, use AttachmentList instead.
 *
 * @param {Object} props
 * @param {Object|Object[]} props.files - Single file object or array of files
 * @param {string} props.themeColor - Theme color for styling
 */
const FileMedia = ({ files, themeColor }) => {
  // Normalize to array
  const fileList = useMemo(() => {
    if (!files) return [];
    return Array.isArray(files) ? files : [files];
  }, [files]);

  // Categorize files by type
  const categorizedFiles = useMemo(() => {
    const images = [];
    const videos = [];
    const audios = [];
    const embeds = [];

    fileList.forEach((file) => {
      if (!file) return;
      const type = getFileType(file);

      switch (type) {
        case 'image':
          if (file.url) images.push(file);
          break;
        case 'video':
          if (file.url) videos.push(file);
          break;
        case 'audio':
          if (file.url) audios.push(file);
          break;
        case 'embed':
          embeds.push(file);
          break;
        default:
          break;
      }
    });

    return { images, videos, audios, embeds };
  }, [fileList]);

  const { images, videos, audios, embeds } = categorizedFiles;

  if (fileList.length === 0) return null;

  return (
    <Container>
      {/* Images - carousel if multiple, single otherwise */}
      {images.length > 1 ? (
        <CarouselWrapper $bgColor={getLighterThemeColor(themeColor)}>
          <Carousel bgColor={getLighterThemeColor(themeColor)} userThemeColor={themeColor} navigation>
            {images.map((file, index) => (
              <CarouselImageItem key={file.id || index} file={file} />
            ))}
          </Carousel>
        </CarouselWrapper>
      ) : (
        images.length === 1 && <SingleImage file={images[0]} />
      )}

      {/* Videos */}
      {videos.map((file, index) => (
        <VideoContainer key={file.id || index}>
          <MediaPlayer previewContent={file.url} themeColor={themeColor} />
        </VideoContainer>
      ))}

      {/* Audio */}
      {audios.map((file, index) => (
        <AudioPlayer key={file.id || index} url={file.url} themeColor={themeColor} />
      ))}

      {/* Embeds (external providers) */}
      {embeds.map((file, index) => (
        <EmbedModule key={file.id || index} params={file} />
      ))}
    </Container>
  );
};

export default FileMedia;
